/**
 * EXPERIMENTAL — DO NOT COPY.
 *
 * Machine enrollment and machine authentication for one internal account. This
 * code has not been through a full security review. It is safe only because
 * `machineFrontDoor.ts` (shared secret + source-IP allowlist) and the Statsig gate
 * run before any of it. Do not reuse its patterns — codes, credential hashing,
 * the confirmation flow, socket auth — for any other auth surface. Start from
 * the reviewed session auth in `api/src/security/` instead.
 */

import { z } from "zod";

/**
 * The wire contract between the okthx daemon and the two endpoints it calls.
 *
 * Both are reached without a session, because a machine that has not enrolled
 * yet has no credential and a machine that has one is not a user. What each
 * request carries is therefore the whole of its authority, which is why the
 * bodies below hold one secret and nothing else that could widen it — no
 * account id, no user id, no session id. Those come from the row the secret
 * matches.
 */

/**
 * The gate on everything to do with the user's own machines.
 *
 * Here rather than beside the agent's module registry because both halves of
 * the feature read it — the backend decides whether the module loads and
 * whether `/api/a/machines` answers, and web decides whether the Computers tab
 * exists. This directory is the one place both can import from, so it is also
 * the one place the gate has a single spelling.
 */
export const MY_COMPUTER_GATE = "agent-bring-your-own-computer";

/** The longest machine name worth storing. A label, not an identifier. */
export const MAX_MACHINE_NAME_CHARS = 64;

export const enrollMachineReqSchema = z.object({
  /** The enrollment code the user revealed on the card, single-use. */
  code: z.string().min(1).max(256),
  /** What the machine calls itself. Self-reported, and only ever a label. */
  machineName: z.string().min(1).max(MAX_MACHINE_NAME_CHARS),
});

export type EnrollMachineReq = z.infer<typeof enrollMachineReqSchema>;

export const enrollMachineRespSchema = z.object({
  /** The machine's durable credential. Returned once and stored only as a hash. */
  credential: z.string(),
  /** The name as stored, which may be shorter than the one sent. */
  machineName: z.string(),
  /**
   * The code the machine displays, for its user to type back into okthx.
   *
   * Minted here rather than on the machine: CASA 1.3.3 requires an activation
   * code to be securely random, and a code the daemon generated would put that
   * on the daemon's own RNG. The machine only shows what it was handed.
   */
  confirmationCode: z.string(),
});

export type EnrollMachineResp = z.infer<typeof enrollMachineRespSchema>;

/**
 * What the mint route hands back: a code that works for a few minutes, and
 * nothing else.
 *
 * This is the only place the code exists on our side. It is not on any row in
 * plaintext and not in any message — so this response is the whole of its
 * distribution, and the settings page holds it in component state rather than a
 * query cache, for the same reason the desktop link is handed over rather than
 * returned.
 */
export const mintConnectionCodeRespSchema = z.object({
  /** The single-use connection code. */
  code: z.string(),
  /**
   * The command to run on the machine. The code is **not** interpolated into
   * it: the daemon prompts for the code on stdin, and a one-liner carrying it
   * would put the code into shell history, which is the thing reading stdin
   * exists to avoid.
   */
  command: z.string(),
  /** How long the code lasts, so the page need not hardcode it. */
  expiresInMinutes: z.number(),
});

export type MintConnectionCodeResp = z.infer<typeof mintConnectionCodeRespSchema>;

/**
 * Minting takes no arguments.
 *
 * The page states what connecting a machine grants and keeps the button
 * disabled until the box is ticked, and that is a step in the interface rather
 * than a server-side precondition. A `consented: true` the client always sends
 * proves nothing and records nothing, so it is not asked for — the honest shape
 * is no field at all rather than one that looks like a control.
 */
export const mintConnectionCodeReqSchema = z.object({});

export type MintConnectionCodeReq = z.infer<typeof mintConnectionCodeReqSchema>;

/**
 * The machine id in the path, for the two routes that take one.
 *
 * Declared here and checked by `zValidator` rather than parsed inside each
 * handler: two handlers deciding for themselves what counts as an id is two
 * chances to disagree, and this is the shape the rest of the API validates in.
 * Coerced because a path parameter is always a string.
 */
export const machineIdParamSchema = z.object({
  machineId: z.coerce.number().int().positive(),
});

export type MachineIdParam = z.infer<typeof machineIdParamSchema>;

export const confirmMachineReqSchema = z.object({
  /** The code the machine displayed. Case and grouping are normalized server-side. */
  confirmationCode: z.string().min(1).max(64),
});

export type ConfirmMachineReq = z.infer<typeof confirmMachineReqSchema>;

export const confirmMachineRespSchema = z.object({
  machineId: z.number(),
});

export type ConfirmMachineResp = z.infer<typeof confirmMachineRespSchema>;

/** One machine as the settings list shows it. */
export const machineRosterEntrySchema = z.object({
  id: z.number(),
  machineName: z.string(),
  /** `pending` is waiting to be confirmed; `expired` has passed its 30 days. */
  status: z.enum(["pending", "active", "expired"]),
  /**
   * The last time this machine exchanged its credential.
   *
   * Not "connected right now", which cannot be answered honestly: the daemon
   * registry is process-local, and in production the socket service is a
   * different process from the API, so a live flag would be right in dev and
   * wrong in prod.
   */
  lastSeenAt: z.string().nullable(),
  /** When the connection lapses. Null while the machine is still pending. */
  expiresAt: z.string().nullable(),
});

export const machineRosterRespSchema = z.object({
  machines: z.array(machineRosterEntrySchema),
});

export type MachineRosterResp = z.infer<typeof machineRosterRespSchema>;

/**
 * Why a machine's credential was refused.
 *
 * Not an HTTP body: it crosses as the `data` on a socket.io handshake error,
 * because the credential is only ever judged by the handshake. The daemon's own
 * copy of these four is in `daemon/src/session.ts`.
 */
export const machineRefusalReasonSchema = z.enum(["unknown", "pending", "expired", "revoked"]);

export type MachineRefusalReason = z.infer<typeof machineRefusalReasonSchema>;
